import { useState, useCallback } from 'react';
import { Check, FlaskConical, AlertTriangle, Tag } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { householdCompounds } from '@/config/experiments/chem1131/experiment1.config';

const stepInfoContent: Record<number, { title: string; content: string }> = {
  1: {
    title: 'Lab Safety in Qualitative Analysis',
    content: `Safety is paramount when working with household chemicals. While these compounds are familiar, they require careful handling in concentrated forms.

**Key Hazards:**
- **NaOH (Sodium Hydroxide)**: Highly caustic - causes severe burns on contact with skin
- **Chemical Splashes**: Always wear safety goggles to protect eyes
- **Ingestion Risk**: Never taste chemicals, even "edible" ones like table salt

**Best Practices:**
- Work in a well-ventilated area
- Keep work area clean and organized
- Know the location of safety equipment (eyewash, fire extinguisher)
- Report any spills or accidents immediately`
  },
  2: {
    title: 'Test Tube Labeling Techniques',
    content: `Proper labeling prevents confusion and errors during experiments. In qualitative analysis, mixing up samples invalidates your results.

**Labeling Best Practices:**
- Use a permanent marker or wax pencil
- Write the chemical formula, not common names (more precise)
- Number tubes sequentially (1-10) as backup identification
- Apply labels before adding any chemicals

**Why 10 Test Tubes?**
Each of the 10 household compounds needs its own tube:
- Sodium chloride (NaCl) - Table salt
- Sodium bicarbonate (NaHCO₃) - Baking soda
- Sodium carbonate (Na₂CO₃) - Washing soda
- And 7 more compounds to identify...`
  },
  3: {
    title: 'Measuring Solid Compounds',
    content: `Adding consistent amounts (a pea-sized amount) of each compound ensures comparable results across all tests.

**Measurement Technique:**
- Use a clean scoopula for each compound (prevents cross-contamination)
- A "pea-sized amount" is the standard measure for each compound
- Level off excess material for consistency

**Visual Observation:**
Before adding any reagents, note the appearance of each solid:
- Color (white, off-white, translucent)
- Texture (crystalline, powdery, granular)
- Crystal shape (cubic, needle-like, amorphous)

These observations can provide initial clues for identification!`
  }
};

export default function Part1Setup() {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [safetyAcknowledged, setSafetyAcknowledged] = useState(false);
  const [tubesLabeled, setTubesLabeled] = useState(false);
  const [compoundsAdded, setCompoundsAdded] = useState(false);
  const [labeledTubes, setLabeledTubes] = useState<Record<string, boolean>>({});
  const [currentStep, setCurrentStep] = useState(1);

  const compounds = Object.values(householdCompounds);
  const completedSteps = [safetyAcknowledged, tubesLabeled, compoundsAdded].filter(Boolean).length;

  const handleSafetyAcknowledge = useCallback(() => {
    if (!safetyAcknowledged) {
      setSafetyAcknowledged(true);
      setCurrentStep(2);
    }
  }, [safetyAcknowledged]);

  const handleLabelTube = useCallback((compoundId: string) => {
    if (safetyAcknowledged && !tubesLabeled) {
      setLabeledTubes(prev => {
        const updated = { ...prev, [compoundId]: true };
        // Check if all tubes are labeled
        if (Object.keys(updated).length === compounds.length) {
          setTubesLabeled(true);
          setCurrentStep(3);
        }
        return updated;
      });
    }
  }, [safetyAcknowledged, tubesLabeled, compounds.length]);

  const handleAddCompounds = useCallback(() => {
    if (tubesLabeled && !compoundsAdded) {
      setCompoundsAdded(true);
    }
  }, [tubesLabeled, compoundsAdded]);

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part I: Setup & Labeling</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Safety Check', hint: 'Review lab safety procedures', done: safetyAcknowledged },
            { id: 2, title: 'Label Test Tubes', hint: 'Label 10 tubes for each compound', done: tubesLabeled },
            { id: 3, title: 'Add Compounds', hint: 'Add a pea-sized amount of each compound', done: compoundsAdded }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Compound List */}
        <div className="compound-list">
          <h4>10 Compounds to Identify:</h4>
          <ul>
            {compounds.map((compound) => (
              <li key={compound.id} className={labeledTubes[compound.id] ? 'labeled' : ''}>
                <span className="formula">{compound.formula}</span>
                <span className="name">{compound.commonName}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <LabBench
        experimentName="Household Compounds Setup"
        completedCount={completedSteps}
        totalSteps={3}
        equipment={['Test tube rack', '10 test tubes', 'Scoopula', 'Labels']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="setup-workspace">
          {/* Safety Panel */}
          {currentStep === 1 && !safetyAcknowledged && (
            <div className="safety-panel" onClick={handleSafetyAcknowledge}>
              <AlertTriangle size={48} className="safety-icon" />
              <h3>Safety Review Required</h3>
              <ul className="safety-list">
                <li>Wear safety goggles at all times</li>
                <li>NaOH is caustic - avoid skin contact</li>
                <li>Do not taste or ingest any chemicals</li>
                <li>Work in a well-ventilated area</li>
                <li>Wash hands thoroughly after experiment</li>
              </ul>
              <button className="acknowledge-btn">
                I Understand - Continue
              </button>
            </div>
          )}

          {/* Test Tube Rack */}
          {safetyAcknowledged && (
            <div className="tube-rack-section">
              <div className="tube-rack">
                {compounds.map((compound, index) => (
                  <div
                    key={compound.id}
                    className={`tube-slot ${labeledTubes[compound.id] ? 'labeled' : ''} ${
                      currentStep === 2 && !labeledTubes[compound.id] ? 'clickable' : ''
                    } ${compoundsAdded ? 'filled' : ''}`}
                    onClick={() => handleLabelTube(compound.id)}
                  >
                    <svg viewBox="0 0 30 80" width={30} height={80}>
                      {/* Test tube */}
                      <rect x="5" y="5" width="20" height="55" rx="2" fill="rgba(200,220,240,0.3)" stroke="#7A9BB8" strokeWidth="1.5" />
                      <ellipse cx="15" cy="60" rx="10" ry="4" fill="rgba(200,220,240,0.4)" stroke="#7A9BB8" strokeWidth="1" />

                      {/* Content if compounds added */}
                      {compoundsAdded && labeledTubes[compound.id] && (
                        <rect x="7" y="40" width="16" height="18" fill="#F5F5F5" opacity="0.9" rx="1" />
                      )}

                      {/* Label */}
                      {labeledTubes[compound.id] && (
                        <rect x="7" y="15" width="16" height="12" fill="#FFFACD" stroke="#DAA520" strokeWidth="0.5" rx="1" />
                      )}
                    </svg>
                    <span className="tube-number">{index + 1}</span>
                    {labeledTubes[compound.id] && (
                      <span className="tube-label">{compound.formula}</span>
                    )}
                  </div>
                ))}
              </div>

              {tubesLabeled && !compoundsAdded && (
                <button className="add-compounds-btn" onClick={handleAddCompounds}>
                  <Tag size={16} />
                  Add Compounds to Tubes
                </button>
              )}
            </div>
          )}

          {/* Status Messages */}
          <div className="status-area">
            {safetyAcknowledged && !tubesLabeled && (
              <div className="status-message info">
                <Tag size={20} />
                Click each test tube to label it ({Object.keys(labeledTubes).length}/{compounds.length} labeled)
              </div>
            )}
            {tubesLabeled && !compoundsAdded && (
              <div className="status-message info">
                <FlaskConical size={20} />
                Click the button to add compounds to each labeled tube
              </div>
            )}
            {compoundsAdded && (
              <div className="status-message success">
                <Check size={20} />
                Setup complete! All tubes labeled and compounds added.
              </div>
            )}
          </div>

          {/* Theory Box */}
          <div className="theory-box">
            <h4>Qualitative Analysis</h4>
            <p>
              Qualitative analysis identifies what substances are present without measuring exact amounts.
              We will use physical properties (solubility) and chemical tests (indicators, acid reactions)
              to systematically identify each compound.
            </p>
          </div>
        </div>

        <style>{`
          .setup-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .safety-panel {
            background: var(--bg-elevated);
            border: 2px solid #F59E0B;
            border-radius: 12px;
            padding: 24px;
            text-align: center;
            max-width: 450px;
            cursor: pointer;
            transition: all 0.2s;
          }

          .safety-panel:hover {
            border-color: var(--secondary);
            transform: translateY(-2px);
          }

          .safety-icon {
            color: #F59E0B;
            margin-bottom: 12px;
          }

          .safety-panel h3 {
            margin: 0 0 16px 0;
            color: var(--text-primary);
          }

          .safety-list {
            text-align: left;
            margin: 0 0 20px 0;
            padding-left: 20px;
          }

          .safety-list li {
            margin: 8px 0;
            color: var(--text-secondary);
            font-size: 14px;
          }

          .acknowledge-btn {
            background: var(--secondary);
            color: white;
            border: none;
            padding: 10px 24px;
            border-radius: 6px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
          }

          .acknowledge-btn:hover {
            background: var(--secondary-dark);
          }

          .tube-rack-section {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 20px;
          }

          .tube-rack {
            display: flex;
            gap: 8px;
            background: linear-gradient(180deg, #5C534A 0%, #3D352F 100%);
            padding: 15px 20px;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.3);
          }

          .tube-slot {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 4px;
            padding: 5px;
            border-radius: 4px;
            transition: all 0.2s;
          }

          .tube-slot.clickable {
            cursor: pointer;
          }

          .tube-slot.clickable:hover {
            background: rgba(255,255,255,0.1);
          }

          .tube-slot.labeled svg rect:first-child {
            stroke: var(--secondary);
            stroke-width: 2;
          }

          .tube-number {
            font-size: 10px;
            color: #999;
          }

          .tube-label {
            font-size: 9px;
            color: var(--secondary);
            font-weight: 600;
            max-width: 30px;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .add-compounds-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            background: var(--secondary);
            color: white;
            border: none;
            padding: 10px 20px;
            border-radius: 6px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
          }

          .add-compounds-btn:hover {
            background: var(--secondary-dark);
          }

          .compound-list {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 12px;
            margin-top: 16px;
          }

          .compound-list h4 {
            margin: 0 0 8px 0;
            font-size: 12px;
            color: var(--text-secondary);
          }

          .compound-list ul {
            list-style: none;
            padding: 0;
            margin: 0;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 4px;
          }

          .compound-list li {
            font-size: 11px;
            padding: 4px 6px;
            border-radius: 4px;
            display: flex;
            gap: 8px;
          }

          .compound-list li.labeled {
            background: rgba(0, 168, 107, 0.1);
          }

          .compound-list .formula {
            font-weight: 600;
            color: var(--secondary);
            min-width: 70px;
          }

          .compound-list .name {
            color: var(--text-secondary);
          }

          .status-area {
            min-height: 50px;
            display: flex;
            justify-content: center;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.info {
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .theory-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 500px;
            border-left: 3px solid var(--secondary);
          }

          .theory-box h4 {
            margin: 0 0 8px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .theory-box p {
            margin: 0;
            font-size: 13px;
            color: var(--text-secondary);
            line-height: 1.5;
          }
        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>
      </LabBench>
      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp1"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
